<template>
  <Teleport to="body">
    <Transition name="confirm-fade">
      <div
        v-if="confirmState.open"
        class="admin-modal confirm-overlay"
        @click.self="onCancel"
      >
        <div
          class="card confirm-card"
          role="alertdialog"
          aria-modal="true"
          :aria-label="confirmState.title"
        >
          <h2 class="confirm-title">{{ confirmState.title }}</h2>
          <p class="confirm-message">{{ confirmState.message }}</p>
          <div class="confirm-actions">
            <button class="btn-secondary confirm-btn" type="button" @click="onCancel">
              {{ confirmState.cancelText }}
            </button>
            <button
              ref="confirmButton"
              class="confirm-btn"
              :class="confirmState.danger ? 'btn-danger' : 'btn-primary'"
              type="button"
              @click="onConfirm"
            >
              {{ confirmState.confirmText }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { confirmState, respondConfirm } from '../composables/useConfirm'

const confirmButton = ref<HTMLButtonElement | null>(null)

const onConfirm = () => respondConfirm(true)
const onCancel = () => respondConfirm(false)

const onKeydown = (event: KeyboardEvent) => {
  if (!confirmState.open) return
  if (event.key === 'Escape') {
    event.preventDefault()
    onCancel()
  } else if (event.key === 'Enter') {
    event.preventDefault()
    onConfirm()
  }
}

watch(
  () => confirmState.open,
  (open) => {
    if (open) {
      nextTick(() => confirmButton.value?.focus())
    }
  },
)

onMounted(() => window.addEventListener('keydown', onKeydown))
onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown))
</script>

<style scoped>
.confirm-overlay {
  /* .admin-modal 已提供固定遮罩与居中，这里只补充层级，保证盖在所有内容之上 */
  z-index: 1000;
}

.confirm-card {
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-width: 420px;
  width: 100%;
}

.confirm-title {
  color: var(--ink);
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.4;
}

.confirm-message {
  color: var(--ink-2);
  font-size: 0.95rem;
  line-height: 1.65;
  overflow-wrap: anywhere;
}

.confirm-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.confirm-btn {
  flex: 0 0 auto;
  min-width: 96px;
  width: auto;
}

.confirm-fade-enter-active,
.confirm-fade-leave-active {
  transition: opacity 0.2s ease;
}

.confirm-fade-enter-from,
.confirm-fade-leave-to {
  opacity: 0;
}

@media (max-width: 480px) {
  .confirm-actions {
    flex-direction: column-reverse;
  }

  .confirm-btn {
    width: 100%;
  }
}
</style>
